// ProformaPage — full-page form for creating a new Proforma Invoice, or for
// editing an existing one when mounted on /app/sales/proforma/:id/edit.
// Mirrors the QuotationCreateForm full-page pattern.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUser } from '@/shared/hooks/useAuth';
import { useCompanyProfile } from '@/features/recruitment/api';
import { useInvoiceRequestActions } from '@/hooks/sales/useInvoiceRequests';
import { ProformaInvoiceGenerator } from '@/components/sales/ProformaInvoiceGenerator';
import { api } from '@/shared/api/axios';
import { getApiErrorMessage } from '@/shared/utils/apiError';

export function ProformaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Edit mode: the route carries the id of the proforma document to load.
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const currentUser = useUser();
  const companyProfile = useCompanyProfile().data;
  const actions = useInvoiceRequestActions();

  // Determine if we're in "create from quotation" mode
  const quotationId = searchParams.get('quotationId') || undefined;
  const quotationNumber = searchParams.get('quotationNumber') || undefined;
  const scheduleId = searchParams.get('scheduleId') || undefined;
  const dealId = searchParams.get('dealId') || undefined;
  const milestoneIndex = searchParams.get('milestoneIndex') ? Number(searchParams.get('milestoneIndex')) : undefined;
  const milestonePhaseLabel = searchParams.get('milestonePhaseLabel') || undefined;
  const fromDeal = (location.state as any)?.fromDeal;
  const isFromDeal = Boolean(fromDeal);

  // Edit mode: fetch the proforma document fresh from the API so the form is
  // always populated with the server's current state, never stale list data.
  const {
    data: proformaData,
    isLoading: proformaLoading,
    isError: proformaError,
  } = useQuery({
    queryKey: ['invoice-requests', 'detail', editId],
    queryFn: async () => {
      const { data } = await api.get(`/invoice-requests/${editId}`, { skipErrorToast: true });
      return data;
    },
    enabled: isEdit,
    retry: false,
    // The generator re-populates every field whenever the editingDraft object
    // identity changes — keep the fetched object stable for the form's lifetime.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
  const editingDraft = useMemo(
    () => (isEdit ? ((proformaData?.request ?? null) as any) : null),
    [isEdit, proformaData]
  );
  const editingProformaNumber = editingDraft?.number || editingDraft?.proformaNumber || '';

  // Fetch deal data if dealId is provided
  const [dealData, setDealData] = useState<any>(null);
  const [dealsLoading, setDealsLoading] = useState(false);

  useEffect(() => {
    if (isEdit) return;
    if (!dealId && !fromDeal) return;
    (async () => {
      setDealsLoading(true);
      try {
        let d = fromDeal;
        if (!d && dealId) {
          const res = await api.get(`/deals?limit=1`);
          const list = res?.data?.deals || res?.data?.data || res?.data?.items || res?.data || [];
          d = Array.isArray(list) ? list.find((x: any) => (x._id || x.id) === dealId) : null;
        }
        if (d) setDealData(d);
      } catch { /* silent */ }
      setDealsLoading(false);
    })();
  }, [isEdit, dealId, fromDeal]);

  // CRM clients list for the Deal selector (create mode only)
  const [availableClients, setAvailableClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(!isEdit);
  const [selectedDealId, setSelectedDealId] = useState(dealId || fromDeal?._id || fromDeal?.id || '');
  const [showDealSelect, setShowDealSelect] = useState(!isFromDeal && !dealId);

  useEffect(() => {
    if (isEdit) return;
    (async () => {
      try {
        const res = await api.get('/deals?limit=100');
        const dealsList = res?.data?.deals || res?.data?.data || res?.data?.items || res?.data || [];
        const clients: any[] = [];
        if (Array.isArray(dealsList)) {
          for (const d of dealsList) {
            const id = d._id || d.id;
            const clientField = d.client || d.company || d.business_name || d.clientName || '';
            const title = d.title ? String(d.title).replace(/^Deal:\s*/i, '') : '';
            const company = clientField || title || 'Deal';
            let contactPerson = d.contactPerson || d.contact_name || '';
            let email = d.contactEmail || d.email || '';
            let phone = d.contactPhone || d.phone || '';
            if (d.notes && typeof d.notes === 'string') {
              if (!contactPerson) { const m = d.notes.match(/Contact:\s*([^,\n\)]+)/i); if (m) contactPerson = m[1].trim(); }
              if (!email) { const m = d.notes.match(/Email:\s*([^,\n\)]+)/i); if (m) email = m[1].trim(); }
              if (!phone) { const m = d.notes.match(/Phone:\s*([^,\n\)]+)/i); if (m) phone = m[1].trim(); }
            }
            const srvs: string[] = Array.isArray(d.services) && d.services.length > 0
              ? d.services
              : (d.service_required ? d.service_required.split(',').map((s: string) => s.trim()).filter(Boolean)
              : (d.serviceRequired ? d.serviceRequired.split(',').map((s: string) => s.trim()).filter(Boolean) : []));
            const val = Number(d.value ?? d.amount ?? 0) || 0;
            clients.push({ id, type: 'deal', name: company, company, contactPerson: contactPerson || company, email: email || '', phone: phone || '', address: d.clientAddress || d.billingAddress || d.address || '', gstin: d.clientGstin || d.gstin || '', state: d.placeOfSupply || d.clientState || '', value: val, description: d.description || d.notes || '', services: srvs });
          }
        }
        setAvailableClients(clients);
      } catch { /* silent */ }
      setClientsLoading(false);
    })();
  }, [isEdit]);

  const handleSelectDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    if (dealId) {
      const c = availableClients.find(x => x.id === dealId);
      if (c) setDealData(c);
      setShowDealSelect(false);
    } else {
      setDealData(null);
    }
  };

  const handleSuccess = () => {
    navigate('/app/sales/proforma-invoices');
  };

  if (!isEdit && (dealsLoading || clientsLoading)) {
    return <div className="p-6"><Spinner /></div>;
  }

  if (isEdit && proformaLoading) {
    return <div className="p-6"><Spinner /></div>;
  }

  if (isEdit && (proformaError || !editingDraft)) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            {proformaError ? 'Failed to load the proforma invoice.' : 'Proforma invoice not found.'}
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/app/sales/proforma-invoices')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Proforma Invoices
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      {/* Top bar */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app/sales/proforma-invoices')}
              className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />
            <div>
              <h1 className="text-base font-bold text-slate-900 dark:text-white">
                {isEdit ? 'Edit Proforma Invoice' : 'New Proforma Invoice'}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isEdit
                  ? `Update the details below and save your changes${editingProformaNumber ? ` — ${editingProformaNumber}` : ''}`
                  : 'Fill in the details below to create a proforma invoice'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto py-6 px-2 sm:px-0">
        {isEdit ? (
          <ProformaInvoiceGenerator
            key={editingDraft?.id || editingDraft?._id || editId}
            asPage={true}
            editingDraft={editingDraft}
            isResubmission={false}
            isRevision={false}
            onClose={() => navigate('/app/sales/proforma-invoices')}
            onSuccess={handleSuccess}
          />
        ) : (
          <ProformaInvoiceGenerator
            asPage={true}
            fromDeal={dealData}
            initialQuotationId={quotationId}
            initialQuotationNumber={quotationNumber}
            initialScheduleId={scheduleId}
            initialMilestoneIndex={milestoneIndex}
            initialMilestonePhaseLabel={milestonePhaseLabel}
            editingDraft={null}
            isResubmission={false}
            isRevision={false}
            onClose={() => navigate('/app/sales/proforma-invoices')}
            onSuccess={handleSuccess}
          />
        )}
      </div>
    </div>
  );
}

export default ProformaPage;
